import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Fx from "@/pages/Fx";
import FuelSettings from "@/pages/FuelSettings";
import { Coins, Fuel, RefreshCw, Clock, Save, Banknote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { isMarketFamilySector } from "@/lib/sectorVisibility";
import { CURRENCIES, setActiveCurrency } from "@/lib/company";

// Kur & Yakıt — Döviz kurları ve yakıt fiyatları tek sekmede birleştirildi.
export default function MarketSettings() {
  const { can, feature, activeSector } = useAuth();
  const secDef = isMarketFamilySector(activeSector);
  const canFx = can("fx.manage") && feature("module.market_pricing", secDef);
  const canFuel = can("logistics.manage") && feature("module.fuel_pricing", secDef);
  const [sub, setSub] = useState(canFx ? "fx" : "fuel");
  const [state, setState] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const [sched, setSched] = useState(null);
  const [schedBusy, setSchedBusy] = useState(false);
  const [currency, setCurrency] = useState("TRY");
  const [curBusy, setCurBusy] = useState(false);
  const canCurrency = can("config.manage");
  useEffect(() => { if (canCurrency) api.get("/settings/currency").then((r) => setCurrency(r.data?.code || "TRY")).catch(() => {}); }, [canCurrency]);
  const saveCurrency = async (code) => {
    setCurBusy(true);
    try {
      const { data } = await api.put("/settings/currency", { code });
      setCurrency(data.code); setActiveCurrency(data.code);
      toast.success(`Sistem para birimi: ${CURRENCIES[data.code]?.label} (${data.symbol}). Tüm tutarlar bu sembolle gösterilir.`);
      setTimeout(() => { try { window.location.reload(); } catch (e) {} }, 900);
    } catch (e) { toast.error("Para birimi kaydedilemedi"); }
    finally { setCurBusy(false); }
  };
  useEffect(() => { api.get("/market/refresh-state").then((r) => setState(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (canFx) api.get("/market/schedule").then((r) => setSched(r.data)).catch(() => {}); }, [canFx]);
  useEffect(() => { const t = setInterval(() => setNowTs(Date.now()), 1000); return () => clearInterval(t); }, []);
  const saveSched = async () => {
    setSchedBusy(true);
    try {
      const { data } = await api.put("/market/schedule", sched);
      setSched({ fx_start_hour: data.fx_start_hour, fx_end_hour: data.fx_end_hour, fx_interval_hours: data.fx_interval_hours, fuel_interval_days: data.fuel_interval_days });
      const r = await api.get("/market/refresh-state"); setState(r.data);
      toast.success("Güncelleme zamanlaması kaydedildi");
    } catch (e) { toast.error("Zamanlama kaydedilemedi"); }
    finally { setSchedBusy(false); }
  };

  const tabs = [
    ...(canFx ? [["fx", "Döviz Kurları", Coins]] : []),
    ...(canFuel ? [["fuel", "Yakıt Fiyatları", Fuel]] : []),
  ];
  const fmt = (iso) => {
    if (!iso) return "henüz güncellenmedi";
    try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
  };
  const countdown = (iso) => {
    if (!iso) return "—";
    const diff = new Date(iso).getTime() - nowTs;
    if (diff <= 0) return "birazdan";
    const s = Math.floor(diff / 1000), h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
    const d = Math.floor(h / 24);
    if (d >= 1) return `${d} gün ${h % 24} saat sonra`;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")} sonra`;
  };

  return (
    <div className="space-y-4 fade-in" data-testid="market-settings">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-black tracking-tight text-slate-900">
          <Coins className="h-6 w-6 text-brand-green" /> Kur & Yakıt
        </h1>
        <p className="mt-1 text-sm text-slate-500">Döviz kurları ve yakıt fiyatları tek yerde. Değerler otomatik güncellenir; dilerseniz manuel sabitleyebilirsiniz.</p>
      </div>

      {canCurrency && (
        <div className="rounded-lg border border-brand-green/30 bg-brand-green/5 p-4" data-testid="system-currency-card">
          <p className="flex items-center gap-1.5 text-sm font-bold text-slate-800"><Banknote className="h-4 w-4 text-brand-green" /> Sistem Para Birimi</p>
          <p className="mb-3 mt-0.5 text-[12px] text-slate-500">Programın tüm modüllerinde tutarlar bu para birimi ve sembolüyle gösterilir. Sektörünüze göre seçin.</p>
          <div className="flex flex-wrap gap-2">
            {Object.values(CURRENCIES).map((c) => (
              <button key={c.code} data-testid={`currency-opt-${c.code}`} disabled={curBusy} onClick={() => saveCurrency(c.code)}
                className={`flex min-w-[130px] items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${currency === c.code ? "border-brand-green bg-white ring-2 ring-brand-green/30" : "border-slate-200 bg-white hover:border-brand-green/40"} disabled:opacity-60`}>
                <span className={`flex h-9 w-9 items-center justify-center rounded-md text-lg font-bold ${currency === c.code ? "bg-brand-green text-white" : "bg-slate-100 text-slate-600"}`}>{c.symbol}</span>
                <span><span className="block text-sm font-semibold text-slate-800">{c.code}</span><span className="block text-[11px] text-slate-500">{c.label}</span></span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2" data-testid="market-auto-status">
        {canFx && (
        <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          <RefreshCw className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">Döviz otomatik güncelleme</p>
            <p className="text-[12px] text-emerald-700">{state?.fx_interval || "08:00–02:00 arası ~2 saatte bir"} · Son: <b data-testid="market-fx-last">{fmt(state?.fx_at)}</b></p>
            <p className="text-[12px] text-emerald-700">Sonraki: <b data-testid="market-fx-next">{countdown(state?.fx_next)}</b></p>
          </div>
        </div>
        )}
        {canFuel && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <Clock className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">Yakıt otomatik güncelleme</p>
            <p className="text-[12px] text-amber-700">{state?.fuel_interval || "5 günde bir"} · Son: <b data-testid="market-fuel-last">{fmt(state?.fuel_at)}</b></p>
            <p className="text-[12px] text-amber-700">Sonraki: <b data-testid="market-fuel-next">{countdown(state?.fuel_next)}</b></p>
          </div>
        </div>
        )}
      </div>

      {canFx && sched && (
        <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="market-schedule-panel">
          <p className="flex items-center gap-1.5 text-sm font-bold text-slate-800"><Clock className="h-4 w-4 text-brand-green" /> Döviz Güncelleme Zamanlaması</p>
          <p className="mb-2 mt-0.5 text-[12px] text-slate-500">Otomatik döviz güncellemesinin saat aralığını ve sıklığını <b>siz</b> belirleyin (İstanbul saati). Şu an: <b>{String(sched.fx_start_hour).padStart(2, "0")}:00–{String(sched.fx_end_hour).padStart(2, "0")}:00</b> arası <b>{sched.fx_interval_hours}</b> saatte bir · Yakıt <b>{sched.fuel_interval_days}</b> günde bir.</p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-[12px] font-medium text-slate-600">Başlangıç saati<input data-testid="sched-fx-start" type="number" min="0" max="23" value={sched.fx_start_hour} onChange={(e) => setSched((s) => ({ ...s, fx_start_hour: e.target.value }))} className="mt-1 block h-9 w-24 rounded-md border border-slate-300 px-2 text-sm" /></label>
            <label className="text-[12px] font-medium text-slate-600">Bitiş saati<input data-testid="sched-fx-end" type="number" min="1" max="24" value={sched.fx_end_hour} onChange={(e) => setSched((s) => ({ ...s, fx_end_hour: e.target.value }))} className="mt-1 block h-9 w-24 rounded-md border border-slate-300 px-2 text-sm" /></label>
            <label className="text-[12px] font-medium text-slate-600">Sıklık (saatte bir)<input data-testid="sched-fx-interval" type="number" min="1" max="24" value={sched.fx_interval_hours} onChange={(e) => setSched((s) => ({ ...s, fx_interval_hours: e.target.value }))} className="mt-1 block h-9 w-28 rounded-md border border-slate-300 px-2 text-sm" /></label>
            <label className="text-[12px] font-medium text-slate-600">Yakıt (günde bir)<input data-testid="sched-fuel-days" type="number" min="1" max="60" value={sched.fuel_interval_days} onChange={(e) => setSched((s) => ({ ...s, fuel_interval_days: e.target.value }))} className="mt-1 block h-9 w-28 rounded-md border border-slate-300 px-2 text-sm" /></label>
            <Button data-testid="sched-save" onClick={saveSched} disabled={schedBusy} className="bg-brand-green text-white hover:bg-brand-green/90">{schedBusy ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Kaydet</Button>
          </div>
        </div>
      )}
        <div className="inline-flex rounded-md bg-slate-100 p-1" data-testid="market-subtabs">
          {tabs.map(([k, l, Icon]) => (
            <button key={k} data-testid={`market-sub-${k}`} onClick={() => setSub(k)}
              className={`flex items-center gap-1.5 rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${sub === k ? "bg-white text-brand-green shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              <Icon className="h-4 w-4" /> {l}
            </button>
          ))}
        </div>

      {sub === "fx" && canFx && <div data-testid="market-fx-panel"><Fx /></div>}
      {sub === "fuel" && canFuel && <div data-testid="market-fuel-panel"><FuelSettings /></div>}
    </div>
  );
}
